import React from "react";
import { Link } from "react-router-dom";
import { HiArrowUpRight, HiCheck } from "react-icons/hi2";
import useReveal from "../../hooks/useReveal";
import useSEO, { SITE_URL } from "../../seo/useSEO";
import { breadcrumb } from "../../seo/schemas";

export default function ServicePage({
  slug,
  eyebrow,
  title,
  titleAccent,
  intro,
  capabilities = [],
  process = [],
  outcomes = [],
  faqs = [],
  techHighlights = [],
}) {
  useReveal();

  const path = `/services/${slug}`;

  useSEO({
    title: `${title} ${titleAccent || ""} — Oguzhan Ozcelik`.trim(),
    description: intro?.slice(0, 200),
    path,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_URL}${path}#service`,
          name: `${title} ${titleAccent || ""}`.trim(),
          description: intro,
          url: `${SITE_URL}${path}`,
          provider: { "@id": `${SITE_URL}/#person` },
          areaServed: "Worldwide",
          serviceType: title,
        },
        faqs.length ? {
          "@type": "FAQPage",
          "@id": `${SITE_URL}${path}#faq`,
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        } : null,
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: title, path },
        ]),
      ].filter(Boolean),
    },
    jsonLdId: `service-${slug}-jsonld`,
  });

  return (
    <main>
      <section className="section--hero">
        <div className="container-x reveal">
          <span className="eyebrow">{eyebrow}</span>
          <h1 className="about-hero__title" style={{ marginTop: 10 }}>
            {title}
            {titleAccent && (
              <>
                <br />
                <span className="it text-accent">{titleAccent}</span>
              </>
            )}
          </h1>
          <p className="lead" style={{ maxWidth: "62ch", marginTop: 18 }}>
            {intro}
          </p>
          <div className="hero__cta" style={{ marginTop: 22 }}>
            <Link to="/contact" className="btn">
              Start a project <span className="arrow"><HiArrowUpRight /></span>
            </Link>
            <Link to="/project" className="btn btn--ghost">
              See past work <span className="arrow"><HiArrowUpRight /></span>
            </Link>
          </div>
        </div>
      </section>

      {capabilities.length > 0 && (
        <section className="section section--tight">
          <div className="container-x">
            <div className="section-head reveal">
              <div className="section-head__title">
                <span className="eyebrow">What's included</span>
                <h2>Capabilities</h2>
              </div>
              <div className="section-head__aside">
                <p>Not a fixed package — pick what you need, drop what you don't.</p>
              </div>
            </div>
            <div className="grid grid-2">
              {capabilities.map((c, i) => (
                <article key={i} className="service reveal">
                  <span className="service__num">— {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="service__title">{c.title}</h3>
                  <p className="service__desc">{c.desc}</p>
                  {c.points && (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                      {c.points.map((p, j) => (
                        <li key={j} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: "var(--text-soft)" }}>
                          <HiCheck style={{ color: "var(--accent)", marginTop: 3, flexShrink: 0 }} /> {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {process.length > 0 && (
        <section className="section section--tight">
          <div className="container-x">
            <div className="section-head reveal">
              <div className="section-head__title">
                <span className="eyebrow">How it goes</span>
                <h2>The process</h2>
              </div>
            </div>
            <div className="grid grid-4">
              {process.map((step, i) => (
                <div key={i} className="service reveal" style={{ minHeight: 180 }}>
                  <span className="service__num">Step {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="service__title" style={{ fontSize: "clamp(18px, 1.4vw, 22px)" }}>{step.title}</h3>
                  <p className="service__desc" style={{ marginBottom: 0 }}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {outcomes.length > 0 && (
        <section className="section section--tight">
          <div className="container-x">
            <div className="section-head reveal">
              <div className="section-head__title">
                <span className="eyebrow">Outcomes</span>
                <h2>What you get</h2>
              </div>
            </div>
            <div className="grid grid-3">
              {outcomes.map((o, i) => (
                <div key={i} className="reveal" style={{ padding: 24, border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", background: "var(--bg-elev)" }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>
                    {o.kpi}
                  </div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(22px, 2.6vw, 32px)", letterSpacing: "-0.02em", color: "var(--text)" }}>
                    {o.title}
                  </div>
                  <p style={{ marginTop: 8, color: "var(--text-soft)", fontSize: 14 }}>{o.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {techHighlights.length > 0 && (
        <section className="section section--tight">
          <div className="container-x">
            <div className="section-head reveal">
              <div className="section-head__title">
                <span className="eyebrow">Tech</span>
                <h2>Stack & tools</h2>
              </div>
              <div className="section-head__aside">
                <p>Pragmatic choices, battle-tested in production.</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {techHighlights.map((t, i) => (
                <span key={i} className="chip reveal"><span className="dot" /> {t}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="section section--tight">
          <div className="container-x">
            <div className="section-head reveal">
              <div className="section-head__title">
                <span className="eyebrow">Common questions</span>
                <h2>FAQ</h2>
              </div>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {faqs.map((f, i) => (
                <details key={i} className="reveal" style={{ padding: "18px 22px", border: "1px solid var(--border)", borderRadius: "var(--radius)", background: "var(--bg-elev)" }}>
                  <summary style={{ cursor: "pointer", fontFamily: "var(--font-display)", fontSize: 16, color: "var(--text)", letterSpacing: "-0.01em" }}>
                    {f.q}
                  </summary>
                  <p style={{ marginTop: 12, color: "var(--text-soft)", fontSize: 15, lineHeight: 1.65, marginBottom: 0 }}>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section section--tight">
        <div className="container-x">
          <div className="cta reveal">
            <span className="eyebrow">Next step</span>
            <h2 className="cta__title">
              Ready to start?
              <br />
              <span className="it accent">Let's scope it.</span>
            </h2>
            <p className="cta__sub">
              Send a few sentences about your project — I'll come back with an honest read on fit, timeline and budget.
            </p>
            <div className="hero__cta" style={{ justifyContent: "center" }}>
              <Link className="btn btn--lg" to="/contact">
                Start a conversation <span className="arrow"><HiArrowUpRight /></span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
